import React, { useState, useEffect } from 'react';
import { Card, Upload, Button, message, Space, Image, Form, Input, Switch, Divider, Alert, Collapse } from 'antd';
import { UploadOutlined, MailOutlined, SaveOutlined, SendOutlined, LinkOutlined, PictureOutlined, HomeOutlined, FileTextOutlined, SafetyOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { configAPI, uploadAPI } from '../services/api';

// API_BASE 不含 /api 后缀，因为所有 fetch 调用的 URL 已包含 /api/ 前缀
// 生产环境使用空字符串（相对路径），nginx 会将 /api/ 代理到 hk-api:8080
// 开发环境使用完整地址 http://localhost:18080
const API_BASE = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_API_URL || 'http://localhost:18080');

// 获取 API 基础地址（用于图片等资源URL拼接）
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  if (process.env.NODE_ENV === 'production') {
    return '';
  }
  return 'http://localhost:18080';
};

// 处理图片URL，相对路径转为绝对路径（用于管理后台预览）
const getFullImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // /uploads/ 路径需要加上 API 前缀
  if (url.startsWith('/uploads/')) {
    return `${getApiBaseUrl()}${url}`;
  }
  // 其他 / 开头的路径
  if (url.startsWith('/')) {
    const base = getApiBaseUrl();
    if (base.endsWith('/')) {
      return base + url.substring(1);
    }
    return base + url;
  }
  return url;
};

const Settings = () => {
  const [bgImage, setBgImage] = useState('/bg.png');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [emailLoading, setEmailLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [homepageLoading, setHomepageLoading] = useState(false);
  const [homepageSaving, setHomepageSaving] = useState(false);
  const [cloudauthLoading, setCloudauthLoading] = useState(false);
  const [cloudauthAK, setCloudauthAK] = useState('');
  const [cloudauthSK, setCloudauthSK] = useState('');
  const [form] = Form.useForm();
  const [homepageForm] = Form.useForm();

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await configAPI.getConfigs();
        if (res.code === 0) {
          const config = res.data.list?.find(c => c.config_key === 'model_square_bg');
          if (config) {
            setBgImage(config.config_value);
          }
          
          // 加载邮件配置
          const emailConfigs = [
            'email_enabled', 'email_host', 'email_port', 'email_username',
            'email_password', 'email_from', 'email_from_name', 'email_secure', 'email_test_recipient'
          ];
          const emailValues = {};
          emailConfigs.forEach(key => {
            const conf = res.data.list?.find(c => c.config_key === key);
            if (conf) {
              if (key === 'email_enabled' || key === 'email_secure') {
                emailValues[key] = conf.config_value === 'true';
              } else {
                emailValues[key] = conf.config_value;
              }
            }
          });
          form.setFieldsValue(emailValues);
        }
      } catch (e) {
        console.error('获取配置失败', e);
      } finally {
        setLoading(false);
        setEmailLoading(false);
      }
    };
    fetchConfig();
  }, []);


  // 加载阿里云核验配置
  const fetchCloudAuthConfig = async () => {
    try {
      const res = await configAPI.getConfigs();
      if (res.code === 0) {
        const ak = res.data.list?.find(c => c.config_key === 'cloudauth_access_key_id');
        const sk = res.data.list?.find(c => c.config_key === 'cloudauth_access_key_secret');
        if (ak) setCloudauthAK(ak.config_value);
        if (sk) setCloudauthSK(sk.config_value);
      }
    } catch (e) {
      console.error('获取云核验配置失败', e);
    }
  };

  // 保存阿里云核验配置
  const handleSaveCloudAuth = async () => {
    setCloudauthLoading(true);
    try {
      await configAPI.updateConfigByKey('cloudauth_access_key_id', cloudauthAK);
      await configAPI.updateConfigByKey('cloudauth_access_key_secret', cloudauthSK);
      message.success('阿里云核验配置已保存');
    } catch (e) {
      message.error('保存失败');
    } finally {
      setCloudauthLoading(false);
    }
  };
  // 获取首页配置
  const fetchHomepageConfig = async () => {
    setHomepageLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/api/admin/configs/homepage`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.code === 0) {
        // 解析联系方式 JSON
        let contactInfo = { email: '', phone: '', address: '', hours: '' };
        try {
          if (data.data.contact_us_content) {
            const parsed = JSON.parse(data.data.contact_us_content);
            contactInfo = { ...contactInfo, ...parsed };
          }
        } catch (e) {
          // 如果不是 JSON 格式，保留原值
        }
        
        homepageForm.setFieldsValue({
          footer_developer_links: data.data.footer_developer_links || '',
          footer_friendly_links: data.data.footer_friendly_links || '',
          footer_company_links: data.data.footer_company_links || '',
          footer_copyright: data.data.footer_copyright || '© 2026 灵狐算力 版权所有',
          footer_icp: data.data.footer_icp || '',
          footer_police: data.data.footer_police || '',
          footer_police_code: data.data.footer_police_code || '',
          footer_telecom: data.data.footer_telecom || '',
          footer_biz_id: data.data.footer_biz_id || '',
          login_background: data.data.login_background || '',
          register_background: data.data.register_background || '',
          privacy_policy_content: data.data.privacy_policy_content || getDefaultPrivacyContent(),
          terms_of_service_content: data.data.terms_of_service_content || getDefaultTermsContent(),
          about_us_content: data.data.about_us_content || getDefaultAboutContent(),
          contact_email: contactInfo.email,
          contact_phone: contactInfo.phone,
          contact_address: contactInfo.address,
          contact_hours: contactInfo.hours,
          hero_banner: data.data.hero_banner || '',
          hero_banner_link: data.data.hero_banner_link || '',
        });
      }
    } catch (error) {
      console.error('获取首页配置失败', error);
    } finally {
      setHomepageLoading(false);
    }
  };

  // 默认隐私政策内容
  const getDefaultPrivacyContent = () => `<h1>隐私政策</h1>
<p>最后更新：2026年1月1日</p>
<h2>1. 信息收集</h2>
<p>我们收集您在使用服务时提供的信息，包括但不限于：</p>
<ul>
  <li>账户信息（邮箱、密码等）</li>
  <li>使用数据（API调用记录、消费历史等）</li>
  <li>设备信息（IP地址、浏览器类型等）</li>
</ul>
<h2>2. 信息使用</h2>
<p>我们使用收集的信息用于：</p>
<ul>
  <li>提供和维护我们的服务</li>
  <li>处理您的订单和支付</li>
  <li>改善我们的服务质量</li>
  <li>与您沟通服务相关事宜</li>
</ul>
<h2>3. 信息保护</h2>
<p>我们采取适当的安全措施保护您的个人信息免受未经授权的访问、使用或披露。</p>
<h2>4. 信息共享</h2>
<p>我们不会向第三方出售您的个人信息。仅在以下情况下可能共享信息：</p>
<ul>
  <li>获得您的明确同意</li>
  <li>遵守法律要求</li>
  <li>保护我们的合法权益</li>
</ul>
<h2>5. 您的权利</h2>
<p>您有权访问、更正或删除您的个人信息。请联系我们行使这些权利。</p>
<h2>6. 联系我们</h2>
<p>如果您对隐私政策有任何问题，请联系我们：</p>
<p>邮箱：privacy@example.com</p>`;

  // 默认服务条款内容
  const getDefaultTermsContent = () => `<h1>服务条款</h1>
<p>最后更新：2026年1月1日</p>
<h2>1. 条款接受</h2>
<p>欢迎使用灵狐算力。您访问或使用我们的服务即表示您同意受这些服务条款的约束。如果您不同意这些条款，请不要使用我们的服务。</p>
<h2>2. 服务描述</h2>
<p>灵狐算力提供AI模型API聚合服务，允许用户通过统一接口访问多个AI模型。我们保留随时修改、暂停或终止服务的权利。</p>
<h2>3. 账户注册</h2>
<p>使用我们的服务需要注册账户。您同意：</p>
<ul>
  <li>提供准确完整的注册信息</li>
  <li>妥善保管您的账户密码</li>
  <li>对您账户下的所有活动负责</li>
  <li>发现任何未经授权使用时请立即通知我们</li>
</ul>
<h2>4. 使用规则</h2>
<p>您同意不：</p>
<ul>
  <li>将服务用于任何非法活动</li>
  <li>干扰或破坏服务的正常运行</li>
  <li>尝试未经授权访问我们的系统</li>
  <li>滥用API配额或进行恶意调用</li>
  <li>转售或重新分发我们的服务</li>
</ul>
<h2>5. 支付与退款</h2>
<p>我们的服务采用预付费模式。您同意：</p>
<ul>
  <li>按公布的价格支付费用</li>
  <li>充值后的余额不可退款</li>
  <li>我们保留提前通知后调整价格的权利</li>
</ul>
<h2>6. 知识产权</h2>
<p>与我们的服务和内容相关的所有知识产权归我们所有。您仅获得有限的使用许可，不得转让或再授权。</p>
<h2>7. 免责声明</h2>
<p>我们的服务按"原样"提供，不提供任何明示或暗示的保证。我们对因使用服务而造成的任何损失不承担责任。</p>
<h2>8. 条款修改</h2>
<p>我们保留随时修改这些条款的权利。修改后的条款将在网站上公布，继续使用服务即表示接受新条款。</p>
<h2>9. 联系我们</h2>
<p>如果您有任何问题，请联系我们：</p>
<p>邮箱：support@example.com</p>`;

  // 默认关于我们内容
  const getDefaultAboutContent = () => `<h1>关于我们</h1>
<h2>公司简介</h2>
<p>灵狐算力是一家专注于AI API聚合服务的科技公司。我们致力于为全球开发者和企业提供稳定、安全、实惠的AI模型接入服务。</p>
<h2>我们的使命</h2>
<p>让每个人都能使用AI技术，降低AI应用开发的门槛，助力企业数字化转型。</p>
<h2>核心优势</h2>
<ul>
  <li><strong>多模型支持</strong> - 一站式接入DeepSeek、智谱AI、豆包等主流AI模型。</li>
  <li><strong>稳定可靠</strong> - 99.9%的服务可用性，智能负载均衡确保业务连续性。</li>
  <li><strong>价格实惠</strong> - 低于官方价格，按量计费无最低门槛，让AI服务更加普惠。</li>
  <li><strong>专业支持</strong> - 7×24小时技术支持，快速响应解决问题。</li>
</ul>
<h2>联系我们</h2>
<p>邮箱：support@example.com</p>
<p>地址：北京市海淀区中关村科技园</p>`;

  useEffect(() => {
    // 延迟加载，确保表单已完全挂载
    const timer = setTimeout(() => {
      fetchHomepageConfig(); fetchCloudAuthConfig();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // 各配置项独立保存函数
  const [savingKeys, setSavingKeys] = useState({});

  // 保存单个配置项
  const saveSingleConfig = async (configKey, configValue) => {
    setSavingKeys(prev => ({ ...prev, [configKey]: true }));
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/api/admin/configs/homepage`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ [configKey]: configValue })
      });
      const data = await response.json();
      if (data.code === 0) {
        message.success('保存成功');
      } else {
        message.error(data.message || '保存失败');
      }
    } catch (error) {
      message.error('保存失败');
    } finally {
      setSavingKeys(prev => ({ ...prev, [configKey]: false }));
    }
  };

  // 保存底部开发者链接
  const handleSaveDeveloperLinks = () => {
    const value = homepageForm.getFieldValue('footer_developer_links');
    saveSingleConfig('footer_developer_links', value || '[]');
  };

  // 保存底部友情链接
  const handleSaveFriendlyLinks = () => {
    const value = homepageForm.getFieldValue('footer_friendly_links');
    saveSingleConfig('footer_friendly_links', value || '[]');
  };

  // 保存底部公司链接
  const handleSaveCompanyLinks = () => {
    const value = homepageForm.getFieldValue('footer_company_links');
    saveSingleConfig('footer_company_links', value || '[]');
  };

  // 保存版权信息
  const handleSaveCopyright = () => {
    const value = homepageForm.getFieldValue('footer_copyright');
    saveSingleConfig('footer_copyright', value || '© 2026 灵狐算力 版权所有');
  };

  // 保存备案信息
  const handleSaveICP = () => {
    const fields = ['footer_icp', 'footer_police', 'footer_police_code', 'footer_telecom', 'footer_biz_id'];
    const saveData = {};
    fields.forEach(f => { saveData[f] = homepageForm.getFieldValue(f) || ''; });
    setSavingKeys(prev => ({ ...prev, footer_icp: true }));
    const token = localStorage.getItem('admin_token');
    fetch(`${API_BASE}/api/admin/configs/homepage`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(saveData)
    }).then(r => r.json()).then(data => {
      if (data.code === 0) message.success('备案信息已保存');
      else message.error(data.message || '保存失败');
    }).catch(() => message.error('保存失败'))
    .finally(() => setSavingKeys(prev => ({ ...prev, footer_icp: false })));
  };

  // 保存 Banner
  const handleSaveBanner = () => {
    const values = homepageForm.getFieldsValue(['hero_banner', 'hero_banner_link']);
    const saveData = {
      hero_banner: values.hero_banner || '',
      hero_banner_link: values.hero_banner_link || '',
    };
    setSavingKeys(prev => ({ ...prev, 'banner': true }));
    (async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const response = await fetch(`${API_BASE}/api/admin/configs/homepage`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(saveData)
        });
        const data = await response.json();
        if (data.code === 0) {
          message.success('保存成功');
        } else {
          message.error(data.message || '保存失败');
        }
      } catch (error) {
        message.error('保存失败');
      } finally {
        setSavingKeys(prev => ({ ...prev, 'banner': false }));
      }
    })();
  };

  // 保存关于我们
  const handleSaveAboutUs = () => {
    const value = homepageForm.getFieldValue('about_us_content');
    saveSingleConfig('about_us_content', value || getDefaultAboutContent());
  };

  // 保存联系我们
  const handleSaveContactUs = () => {
    const values = homepageForm.getFieldsValue(['contact_email', 'contact_phone', 'contact_address', 'contact_hours']);
    const contactInfo = {
      email: values.contact_email || '',
      phone: values.contact_phone || '',
      address: values.contact_address || '',
      hours: values.contact_hours || '',
    };
    saveSingleConfig('contact_us_content', JSON.stringify(contactInfo));
  };

  // 保存隐私政策
  const handleSavePrivacy = () => {
    const value = homepageForm.getFieldValue('privacy_policy_content');
    saveSingleConfig('privacy_policy_content', value || getDefaultPrivacyContent());
  };

  // 保存服务条款
  const handleSaveTerms = () => {
    const value = homepageForm.getFieldValue('terms_of_service_content');
    saveSingleConfig('terms_of_service_content', value || getDefaultTermsContent());
  };

  // 保存登录页背景
  const handleSaveLoginBg = () => {
    const value = homepageForm.getFieldValue('login_background');
    saveSingleConfig('login_background', value || '');
  };

  // 保存注册页背景
  const handleSaveRegisterBg = () => {
    const value = homepageForm.getFieldValue('register_background');
    saveSingleConfig('register_background', value || '');
  };

  const handleUpload = async (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件！');
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('图片大小不能超过 5MB！');
      return false;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await uploadAPI.upload(formData);

      if (res.code === 0) {
        const url = res.data.url;
        await configAPI.updateConfigByKey('model_square_bg', url);
        setBgImage(url);
        message.success('背景图上传成功！');
      } else {
        message.error(res.message || '上传失败');
      }
    } catch (e) {
      message.error('上传失败：' + (e.message || '未知错误'));
    } finally {
      setUploading(false);
    }
    return false;
  };

  // 上传 Banner 图片（自动保存）
  const handleBannerUpload = async (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件！');
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('图片大小不能超过 5MB！');
      return false;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await uploadAPI.upload(formData);

      if (res.code === 0) {
        const newBannerUrl = res.data.url;
        homepageForm.setFieldsValue({ hero_banner: newBannerUrl });
        
        // 自动保存到服务器
        const token = localStorage.getItem('admin_token');
        const currentValues = homepageForm.getFieldsValue();
        const saveData = {
          ...currentValues,
          hero_banner: newBannerUrl,
          footer_developer_links: currentValues.footer_developer_links || '[]',
          footer_friendly_links: currentValues.footer_friendly_links || '[]',
          footer_company_links: currentValues.footer_company_links || '[]',
          footer_copyright: currentValues.footer_copyright || 'Copyright © 2026 Blue API, All rights reserved.',
        };
        
        await fetch(`${API_BASE}/api/admin/configs/homepage`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(saveData)
        });
        
        message.success('Banner 上传并保存成功！');
      } else {
        message.error(res.message || '上传失败');
      }
    } catch (e) {
      message.error('上传失败：' + (e.message || '未知错误'));
    }
    return false;
  };

  // 上传登录页背景图（自动保存）
  const handleLoginBgUpload = async (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件！');
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('图片大小不能超过 5MB！');
      return false;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await uploadAPI.upload(formData);

      if (res.code === 0) {
        const newBgUrl = res.data.url;
        homepageForm.setFieldsValue({ login_background: newBgUrl });
        
        // 自动保存到服务器
        const token = localStorage.getItem('admin_token');
        const currentValues = homepageForm.getFieldsValue();
        const saveData = {
          ...currentValues,
          login_background: newBgUrl,
          footer_developer_links: currentValues.footer_developer_links || '[]',
          footer_friendly_links: currentValues.footer_friendly_links || '[]',
          footer_company_links: currentValues.footer_company_links || '[]',
          footer_copyright: currentValues.footer_copyright || 'Copyright © 2026 Blue API, All rights reserved.',
        };
        
        await fetch(`${API_BASE}/api/admin/configs/homepage`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(saveData)
        });
        
        message.success('登录页背景图上传并保存成功！');
      } else {
        message.error(res.message || '上传失败');
      }
    } catch (e) {
      message.error('上传失败：' + (e.message || '未知错误'));
    }
    return false;
  };

  // 上传注册页背景图（自动保存）
  const handleRegisterBgUpload = async (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件！');
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('图片大小不能超过 5MB！');
      return false;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await uploadAPI.upload(formData);

      if (res.code === 0) {
        const newBgUrl = res.data.url;
        homepageForm.setFieldsValue({ register_background: newBgUrl });
        
        // 自动保存到服务器
        const token = localStorage.getItem('admin_token');
        const currentValues = homepageForm.getFieldsValue();
        const saveData = {
          ...currentValues,
          register_background: newBgUrl,
          footer_developer_links: currentValues.footer_developer_links || '[]',
          footer_friendly_links: currentValues.footer_friendly_links || '[]',
          footer_company_links: currentValues.footer_company_links || '[]',
          footer_copyright: currentValues.footer_copyright || 'Copyright © 2026 Blue API, All rights reserved.',
        };
        
        await fetch(`${API_BASE}/api/admin/configs/homepage`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(saveData)
        });
        
        message.success('注册页背景图上传并保存成功！');
      } else {
        message.error(res.message || '上传失败');
      }
    } catch (e) {
      message.error('上传失败：' + (e.message || '未知错误'));
    }
    return false;
  };

  const handleEmailConfig = async () => {
    setEmailLoading(true);
    try {
      const values = form.getFieldsValue();
      const configs = [
        { key: 'email_enabled', value: String(values.email_enabled || false) },
        { key: 'email_host', value: values.email_host || '' },
        { key: 'email_port', value: String(values.email_port || 587) },
        { key: 'email_username', value: values.email_username || '' },
        { key: 'email_password', value: values.email_password || '' },
        { key: 'email_from', value: values.email_from || '' },
        { key: 'email_from_name', value: values.email_from_name || '' },
        { key: 'email_secure', value: String(values.email_secure || false) },
        { key: 'email_test_recipient', value: values.email_test_recipient || '' },
      ];
      
      for (const config of configs) {
        await configAPI.updateConfigByKey(config.key, config.value);
      }
      message.success('邮件配置保存成功！');
    } catch (e) {
      message.error('保存失败：' + (e.message || '未知错误'));
    } finally {
      setEmailLoading(false);
    }
  };

  const handleTestEmail = async () => {
    const values = form.getFieldsValue();
    if (!values.email_test_recipient) {
      message.error('请输入测试收件人邮箱');
      return;
    }
    
    setTestLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/email/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
        },
        body: JSON.stringify({
          to: values.email_test_recipient,
          host: values.email_host,
          port: parseInt(values.email_port, 10),
          secure: values.email_secure === true || values.email_secure === 'true',
          auth: {
            user: values.email_username,
            pass: values.email_password,
          },
          from: values.email_from,
          from_name: values.email_from_name,
        }),
      });
      const data = await response.json();
      if (data.code === 0) {
        message.success('测试邮件发送成功！');
      } else {
        message.error(data.message || '发送失败');
      }
    } catch (e) {
      message.error('发送失败：' + (e.message || '未知错误'));
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <div>
      <h2 className="page-title">基础配置</h2>

      {/* 邮件服务器配置 */}
      <Card 
        title={
          <span>
            <MailOutlined style={{ marginRight: 8, color: '#667eea' }} />
            邮件服务器配置
          </span>
        }
        style={{ marginBottom: 24 }}
        loading={loading}
      >
        <Form form={form} layout="vertical">
          <Alert
            message="邮件服务说明"
            description="配置 SMTP 邮件服务器以支持用户注册验证码、找回密码等功能。支持 Gmail、QQ邮箱、网易邮箱等主流邮件服务。"
            type="info"
            showIcon
            style={{ marginBottom: 24, borderRadius: 8 }}
          />
          
          <Form.Item
            name="email_enabled"
            label="启用邮件服务"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Divider />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
            <Form.Item
              name="email_host"
              label="SMTP 服务器地址"
              rules={[{ required: true, message: '请输入SMTP服务器' }]}
            >
              <Input placeholder="smtp.gmail.com 或 smtp.qq.com" />
            </Form.Item>

            <Form.Item
              name="email_port"
              label="SMTP 端口"
              rules={[{ required: true, message: '请输入端口' }]}
            >
              <Input placeholder="587 (TLS) 或 465 (SSL)" type="number" />
            </Form.Item>

            <Form.Item
              name="email_username"
              label="用户名/邮箱"
              rules={[{ required: true, message: '请输入用户名' }]}
            >
              <Input placeholder="your-email@example.com" />
            </Form.Item>

            <Form.Item
              name="email_password"
              label="密码/授权码"
              rules={[{ required: true, message: '请输入密码' }]}
            >
              <Input.Password placeholder="邮箱密码或授权码" />
            </Form.Item>

            <Form.Item
              name="email_from"
              label="发件人邮箱"
              rules={[{ required: true, message: '请输入发件人邮箱' }]}
            >
              <Input placeholder="noreply@example.com" />
            </Form.Item>

            <Form.Item
              name="email_from_name"
              label="发件人名称"
              rules={[{ required: false, message: '请输入发件人名称' }]}
            >
              <Input placeholder="灵狐算力" />
            </Form.Item>

            <Form.Item
              name="email_secure"
              label="使用 SSL/TLS"
              valuePropName="checked"
              tooltip="Gmail 和 QQ 企业邮箱建议启用，QQ邮箱个人版建议禁用"
            >
              <Switch />
            </Form.Item>
          </div>

          <Divider />

          <Form.Item
            name="email_test_recipient"
            label="测试收件人邮箱"
          >
            <Space>
              <Input placeholder="输入测试邮箱地址" style={{ width: 250 }} />
              <Button
                icon={<SendOutlined />}
                onClick={handleTestEmail}
                loading={testLoading}
              >
                发送测试邮件
              </Button>
            </Space>
          </Form.Item>

          <Divider />

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleEmailConfig}
              loading={emailLoading}
              size="large"
            >
              保存邮件配置
            </Button>
          </Form.Item>
        </Form>

        <Divider />

        <div style={{ background: '#f6f7ff', padding: 16, borderRadius: 8, fontSize: 13 }}>
          <h4 style={{ marginTop: 0 }}>常见邮件服务配置：</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            <div>
              <strong>Gmail</strong>
              <ul style={{ margin: '8px 0', paddingLeft: 16 }}>
                <li>服务器: smtp.gmail.com</li>
                <li>端口: 587 (TLS)</li>
                <li>需开启"低安全性应用"或使用应用专用密码</li>
              </ul>
            </div>
            <div>
              <strong>QQ 邮箱</strong>
              <ul style={{ margin: '8px 0', paddingLeft: 16 }}>
                <li>服务器: smtp.qq.com</li>
                <li>端口: 587 (TLS)</li>
                <li>需在设置中开启 SMTP 并获取授权码</li>
              </ul>
            </div>
            <div>
              <strong>网易邮箱</strong>
              <ul style={{ margin: '8px 0', paddingLeft: 16 }}>
                <li>服务器: smtp.163.com</li>
                <li>端口: 465 (SSL)</li>
                <li>需开启 SMTP 并获取授权码</li>
              </ul>
            </div>
          </div>
        </div>
      </Card>

      {/* 界面配置 */}
      <Card title="界面配置" loading={loading}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <h4>模型广场背景图</h4>
            <p style={{ color: '#666', marginBottom: 12 }}>
              设置用户端模型广场搜索区域的背景图片，建议尺寸 800x300 像素，支持 JPG、PNG 格式。
            </p>
            
            <Space>
              <Upload
                showUploadList={false}
                beforeUpload={handleUpload}
                accept="image/*"
              >
                <Button 
                  type="primary" 
                  icon={<UploadOutlined />} 
                  loading={uploading}
                  disabled={uploading}
                >
                  {uploading ? '上传中...' : '上传背景图'}
                </Button>
              </Upload>
              
              <Button onClick={() => {
                setBgImage('/bg.png');
                configAPI.updateConfigByKey('model_square_bg', '/bg.png');
                message.success('已恢复默认背景图');
              }}>
                恢复默认
              </Button>
            </Space>
          </div>

          {/* 预览 */}
          <div>
            <h4>预览效果</h4>
            <div style={{
              padding: 24,
              borderRadius: 12,
              backgroundImage: `url(${getFullImageUrl(bgImage)})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              boxShadow: '0 2px 12px rgba(0,0,0,0.15)'
            }}>
              <div style={{
                background: 'rgba(255,255,255,0.95)',
                padding: '16px 24px',
                borderRadius: 8,
                textAlign: 'center'
              }}>
                <span style={{ color: '#666' }}>🔍</span>
                <span style={{ color: '#999', marginLeft: 8 }}>搜索模型名称、描述...</span>
              </div>
            </div>
          </div>
        </Space>
      </Card>

      {/* 首页设置 */}
      <Card
        title={
          <span>
            <HomeOutlined style={{ marginRight: 8, color: '#667eea' }} />
            首页设置
          </span>
        }
        style={{ marginTop: 16 }}
        loading={homepageLoading}
      >
        <Form form={homepageForm} layout="vertical">
          <Collapse
            ghost
            items={[
              {
                key: 'developer',
                label: <span><LinkOutlined /> 底部开发者链接</span>,
                children: (
                  <div>
                    <Alert
                      message="配置格式说明"
                      description={
                        <div style={{ fontSize: 12 }}>
                          <p>请输入 JSON 格式的链接数组，例如：</p>
                          <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
{`[{"title":"API 文档","url":"https://docs.example.com"},{"title":"开发指南","url":"https://guide.example.com"}]`}
                          </pre>
                        </div>
                      }
                      type="info"
                      style={{ marginBottom: 16 }}
                    />
                    <Form.Item name="footer_developer_links">
                      <Input.TextArea rows={4} placeholder='[{"title":"API 文档","url":"#"}]' />
                    </Form.Item>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveDeveloperLinks} loading={savingKeys.footer_developer_links}>
                      保存
                    </Button>
                  </div>
                ),
              },
              {
                key: 'friendly',
                label: <span><LinkOutlined /> 底部友情链接</span>,
                children: (
                  <div>
                    <Alert
                      message="配置格式说明"
                      description={
                        <div style={{ fontSize: 12 }}>
                          <p>请输入 JSON 格式的链接数组，例如：</p>
                          <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
{`[{"title":"DeepSeek","url":"https://www.deepseek.com"},{"title":"智谱AI","url":"https://www.zhipuai.cn"}]`}
                          </pre>
                        </div>
                      }
                      type="info"
                      style={{ marginBottom: 16 }}
                    />
                    <Form.Item name="footer_friendly_links">
                      <Input.TextArea rows={4} placeholder='[{"title":"DeepSeek","url":"https://www.deepseek.com"}]' />
                    </Form.Item>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveFriendlyLinks} loading={savingKeys.footer_friendly_links}>
                      保存
                    </Button>
                  </div>
                ),
              },
              {
                key: 'company',
                label: <span><LinkOutlined /> 底部公司链接（关于我们、联系我们等）</span>,
                children: (
                  <div>
                    <Alert
                      message="配置格式说明"
                      description={
                        <div style={{ fontSize: 12 }}>
                          <p>请输入 JSON 格式的链接数组，支持以下链接：</p>
                          <ul>
                            <li><strong>关于我们</strong> - url 使用 /about</li>
                            <li><strong>联系我们</strong> - url 使用 /contact</li>
                            <li><strong>服务条款</strong> - url 使用 /terms</li>
                            <li><strong>隐私政策</strong> - url 使用 /privacy</li>
                          </ul>
                          <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4 }}>
{`[{"title":"关于我们","url":"/about"},{"title":"联系我们","url":"/contact"},{"title":"服务条款","url":"/terms"},{"title":"隐私政策","url":"/privacy"}]`}
                          </pre>
                        </div>
                      }
                      type="info"
                      style={{ marginBottom: 16 }}
                    />
                    <Form.Item name="footer_company_links">
                      <Input.TextArea rows={4} placeholder='[{"title":"关于我们","url":"/about"}]' />
                    </Form.Item>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveCompanyLinks} loading={savingKeys.footer_company_links}>
                      保存
                    </Button>
                  </div>
                ),
              },
              {
                key: 'copyright',
                label: <span><FileTextOutlined /> 底部版权信息</span>,
                children: (
                  <div>
                    <Alert
                      message="版权信息"
                      description="设置用户端页面底部显示的版权文字，将显示在所有页面的底部版权区域。"
                      type="info"
                      style={{ marginBottom: 16 }}
                    />
                    <Form.Item name="footer_copyright">
                      <Input.TextArea rows={2} placeholder='© 2026 灵狐算力 版权所有' />
                    </Form.Item>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveCopyright} loading={savingKeys.footer_copyright}>
                      保存
                    </Button>
                    <Divider style={{ margin: '24px 0 16px' }} />
                    <Alert
                      message="ICP备案信息"
                      description="设置ICP备案号，如：京ICP备XXXXXXXX号-1，将显示在页面底部。"
                      type="info"
                      style={{ marginBottom: 16 }}
                    />
                    <Form.Item name="footer_icp">
                      <Input placeholder='京ICP备XXXXXXXX号-1' />
                    </Form.Item>
                    <Form.Item name="footer_police">
                      <Input placeholder='公安备案 (如: 京公网安备)' />
                    </Form.Item>
                    <Form.Item name="footer_police_code">
                      <Input placeholder='公安备案号 (如: 11010802044458)' />
                    </Form.Item>
                    <Form.Item name="footer_telecom">
                      <Input placeholder='增值电信许可证 (如: 京B2-20242084)' />
                    </Form.Item>
                    <Form.Item name="footer_biz_id">
                      <Input placeholder='业务标识 (如: Beijing-...S0112)' />
                    </Form.Item>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveICP} loading={savingKeys.footer_icp}>
                      保存备案信息
                    </Button>
                  </div>
                ),
              },
              {
                key: 'banner',
                label: <span><PictureOutlined /> 首页顶部 Banner</span>,
                children: (
                  <div>
                    <Alert
                      message="Banner 设置"
                      description="设置首页顶部横幅图片和点击链接，建议尺寸 1920x200 像素，支持 JPG、PNG 格式。"
                      type="info"
                      style={{ marginBottom: 16 }}
                    />
                    <Form.Item label="Banner 图片" name="hero_banner">
                      <Input.TextArea rows={2} placeholder='https://example.com/banner.png 或 /banner.png' />
                    </Form.Item>
                    <Form.Item>
                      <Space>
                        <Upload
                          accept="image/*"
                          showUploadList={false}
                          beforeUpload={handleBannerUpload}
                        >
                          <Button icon={<UploadOutlined />}>上传 Banner 图片</Button>
                        </Upload>
                        <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveBanner} loading={savingKeys.banner}>
                          保存
                        </Button>
                      </Space>
                    </Form.Item>
                    <Form.Item label="点击链接" name="hero_banner_link" style={{ marginTop: 16 }}>
                      <Input placeholder='https://example.com 或 /pricing' />
                    </Form.Item>
                  </div>
                ),
              },
              {
                key: 'about',
                label: <span><FileTextOutlined /> 关于我们内容</span>,
                children: (
                  <div>
                    <Alert
                      message="支持 HTML 格式"
                      description="关于我们页面 (/about) 将显示此内容"
                      type="info"
                      style={{ marginBottom: 16 }}
                    />
                    <Form.Item name="about_us_content">
                      <Input.TextArea rows={10} placeholder='<h1>关于我们</h1><p>请在此输入关于我们内容...</p>' />
                    </Form.Item>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveAboutUs} loading={savingKeys.about_us_content}>
                      保存
                    </Button>
                  </div>
                ),
              },
              {
                key: 'contact',
                label: <span><MailOutlined /> 联系方式</span>,
                children: (
                  <div>
                    <Alert
                      message="联系方式设置"
                      description="设置联系页面的联系方式信息，包括邮箱、电话、地址等"
                      type="info"
                      style={{ marginBottom: 16 }}
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                      <Form.Item name="contact_email" label="联系邮箱">
                        <Input placeholder="support@blueapi.com" />
                      </Form.Item>
                      <Form.Item name="contact_phone" label="联系电话">
                        <Input placeholder="400-123-4567" />
                      </Form.Item>
                    </div>
                    <Form.Item name="contact_address" label="公司地址">
                      <Input placeholder="北京市海淀区中关村科技园" />
                    </Form.Item>
                    <Form.Item name="contact_hours" label="工作时间">
                      <Input placeholder="周一至周五：9:00 - 18:00" />
                    </Form.Item>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveContactUs} loading={savingKeys.contact_us_content}>
                      保存
                    </Button>
                  </div>
                ),
              },
              {
                key: 'privacy',
                label: <span><FileTextOutlined /> 隐私政策内容</span>,
                children: (
                  <div>
                    <Alert
                      message="支持 HTML 格式"
                      description="注册页面将链接到 /privacy 页面显示此内容"
                      type="info"
                      style={{ marginBottom: 16 }}
                    />
                    <Form.Item name="privacy_policy_content">
                      <Input.TextArea rows={10} placeholder='<h1>隐私政策</h1><p>请在此输入隐私政策内容...</p>' />
                    </Form.Item>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSavePrivacy} loading={savingKeys.privacy_policy_content}>
                      保存
                    </Button>
                  </div>
                ),
              },
              {
                key: 'terms',
                label: <span><FileTextOutlined /> 服务条款内容</span>,
                children: (
                  <div>
                    <Alert
                      message="支持 HTML 格式"
                      description="注册页面将链接到 /terms 页面显示此内容"
                      type="info"
                      style={{ marginBottom: 16 }}
                    />
                    <Form.Item name="terms_of_service_content">
                      <Input.TextArea rows={10} placeholder='<h1>服务条款</h1><p>请在此输入服务条款内容...</p>' />
                    </Form.Item>
                    <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveTerms} loading={savingKeys.terms_of_service_content}>
                      保存
                    </Button>
                  </div>
                ),
              },
              {
                key: 'login-bg',
                label: <span><PictureOutlined /> 登录页背景图</span>,
                children: (
                  <div>
                    <Alert
                      message="登录页背景图设置"
                      description="设置登录页背景图片，建议尺寸 1920x1080 像素，支持 JPG、PNG 格式。"
                      type="info"
                      style={{ marginBottom: 16 }}
                    />
                    <Form.Item name="login_background">
                      <Input.TextArea rows={2} placeholder="https://example.com/login-bg.jpg 或 /uploads/login-bg.jpg" />
                    </Form.Item>
                    <Space>
                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={handleLoginBgUpload}
                      >
                        <Button icon={<UploadOutlined />}>上传登录页背景图</Button>
                      </Upload>
                      <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveLoginBg} loading={savingKeys.login_background}>
                        保存
                      </Button>
                    </Space>
                  </div>
                ),
              },
              {
                key: 'register-bg',
                label: <span><PictureOutlined /> 注册页背景图</span>,
                children: (
                  <div>
                    <Alert
                      message="注册页背景图设置"
                      description="设置注册页背景图片，建议尺寸 1920x1080 像素，支持 JPG、PNG 格式。"
                      type="info"
                      style={{ marginBottom: 16 }}
                    />
                    <Form.Item name="register_background">
                      <Input.TextArea rows={2} placeholder="https://example.com/register-bg.jpg 或 /uploads/register-bg.jpg" />
                    </Form.Item>
                    <Space>
                      <Upload
                        accept="image/*"
                        showUploadList={false}
                        beforeUpload={handleRegisterBgUpload}
                      >
                        <Button icon={<UploadOutlined />}>上传注册页背景图</Button>
                      </Upload>
                      <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveRegisterBg} loading={savingKeys.register_background}>
                        保存
                      </Button>
                    </Space>
                  </div>
                ),
              },
            ]}
          />
        </Form>
      </Card>


      {/* 阿里云身份核验配置 */}
      <Card
        title={
          <span>
            <SafetyOutlined style={{ marginRight: 8, color: '#667eea' }} />
            阿里云身份核验配置
          </span>
        }
        style={{ marginTop: 16 }}
        extra={<span style={{ color: '#999', fontSize: 12 }}>用于个人实名认证自动审核</span>}
      >
        <Alert
          message="配置说明"
          description="用于个人实名认证自动核验姓名与身份证号一致性。核验一致自动通过，不一致自动拒绝。企业认证不受影响。"
          type="info"
          showIcon
          style={{ marginBottom: 24, borderRadius: 8 }}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={{ fontWeight: 500, display: 'block', marginBottom: 4 }}>AccessKey ID</label>
            <Input.Password
              value={cloudauthAK}
              onChange={e => setCloudauthAK(e.target.value)}
              placeholder="请输入阿里云 AccessKey ID"
              iconRender={visible => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
            />
          </div>
          <div>
            <label style={{ fontWeight: 500, display: 'block', marginBottom: 4 }}>AccessKey Secret</label>
            <Input.Password
              value={cloudauthSK}
              onChange={e => setCloudauthSK(e.target.value)}
              placeholder="请输入阿里云 AccessKey Secret"
            />
          </div>
        </div>
        <Divider />
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSaveCloudAuth}
          loading={cloudauthLoading}
        >
          保存核验配置
        </Button>
      </Card>
      {/* 系统信息 */}
      <Card style={{ marginTop: 16 }} title="系统信息">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          <div>
            <div style={{ color: '#999', marginBottom: 4 }}>系统名称</div>
            <div>灵狐算力 管理后台</div>
          </div>
          <div>
            <div style={{ color: '#999', marginBottom: 4 }}>版本</div>
            <div>v1.0.0</div>
          </div>
          <div>
            <div style={{ color: '#999', marginBottom: 4 }}>前端框架</div>
            <div>React 18 + Ant Design 5</div>
          </div>
          <div>
            <div style={{ color: '#999', marginBottom: 4 }}>API地址</div>
            <div>{process.env.REACT_APP_API_URL || 'http://localhost:18080/api'}</div>
          </div>
          <div>
            <div style={{ color: '#999', marginBottom: 4 }}>支付渠道</div>
            <div>微信支付 / 支付宝 / 线下转账</div>
          </div>
          <div>
            <div style={{ color: '#999', marginBottom: 4 }}>币种</div>
            <div>CNY（人民币）</div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Settings;
